/// <amd-module name="Build/DesignerExtensions/YamlSnippetForm/Components/DataSourcePickList" />
import * as React from "react";

import { EnhancedDropdown } from "Build/DesignerExtensions/YamlSnippetForm/Components/EnhancedDropdown";
import { YamlSnippetInput } from "Pipelines/Common/Library/Contracts/DataProvider";
import { ServiceEndpointDetails, IServiceConnectionProvider } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Types";
import { IListBoxItem } from "VSSUI/ListBox";

export interface IDataSourcePickListProps {
    inputDefinition: YamlSnippetInput;
    serviceConnectionProvider: IServiceConnectionProvider;
    onValueChanged(name: string, value: string): void;
    getValue(name: string): string;
    getUnverifiedValues?(parameters: string[]): string[];
    verifyValues?(parameters: string[]): void;
    onError(location: string, error: Error): void;
    serviceEndpointDetails?: ServiceEndpointDetails;
    makeRecursiveDatasourceCalls?: boolean;
    preloadItems?: boolean;
}

/**
 * This component is used for inputs whose resource options are determined by another input. For example,
 * a container registry input might need to go fetch its list of options from an azure subscription. But this
 * component is more generalized than only that specific scenario. Instead it uses the binding information contained
 * within YamlSnippet and IServiceConnectionProvider to figure out how to fetch its dependent data
 */
export class DataSourcePickList extends React.Component<IDataSourcePickListProps, {}> {
    private _lastCheckedDependencies: string;
    private _cachedPromise: Promise<IListBoxItem[]>;
    
    public render(): JSX.Element {
        return <EnhancedDropdown
            inputDefinition={this.props.inputDefinition} 
            onValueChanged={this.props.onValueChanged} 
            getValue={this.props.getValue} 
            loadItems={this._loadItems}
            loadOnMount={this.props.preloadItems}
            alwaysRefresh={true}
            onError={this.props.onError}
            errorContext={"DataSourcePickList"} />
    }

    private _loadItems = async (): Promise<IListBoxItem[]> => {
        const endpointToCall = this.props.getValue(this.props.inputDefinition.dataSourceDependency);
        const parameters = this._getParameters();

        // // if all of our dependent values are unchanged, just return the cached list
        const currentDependencies = endpointToCall + Object.keys(parameters).map(k => parameters[k]);
        if (currentDependencies === this._lastCheckedDependencies && this._cachedPromise) {
            return this._cachedPromise;
        }

        this._lastCheckedDependencies = currentDependencies;
        this._cachedPromise = this._loadItemsInternal(endpointToCall, parameters);
        return this._cachedPromise;
    }

    private _loadItemsInternal = async(endpointToCall: string, parameters: { [key: string]: string }) : Promise<IListBoxItem[]> => {
        const resourceMap = await this.props.serviceConnectionProvider.getDataSourceResources(this.props.inputDefinition, endpointToCall, this.props.serviceEndpointDetails, parameters, this.props.makeRecursiveDatasourceCalls);
        let items: IListBoxItem[] = [];
        Object.keys(resourceMap).forEach(resourceKey => {
            items.push({
                id: resourceKey,
                text: resourceMap[resourceKey]
            });
        });

        items.sort((a, b) => {
            return a.text!.localeCompare(b.text!, navigator.language);
        });

        return items;
    }

    private _getParameters(): { [key: string]: string } {
        const parametersFromUrl = this._extractDependentParametersFromUrl(this.props.inputDefinition.dataSourceBinding.endpointUrl);
        const parameters: { [key: string]: string } = {
            ...parametersFromUrl,
            ...this.props.inputDefinition.dataSourceBinding.parameters
        };

        Object.keys(parameters).forEach(paramName => {
            let paramValue = parameters[paramName];
            if (paramValue.indexOf("$(") === 0) {
                paramValue = this.props.getValue(paramValue.substr(2, paramValue.length - 3));
            }
            parameters[paramName] = paramValue;
        });

        return parameters;
    }

    private _extractDependentParametersFromUrl(url: string): { [key: string]: string; } {
        if (url) {
            const parameters: { [key: string]: string; } = {};
            while (true) {
                const startIndex = url.indexOf('$(');
                if (startIndex < 0) {
                    break;
                }
                url = url.substr(startIndex);
                const param = url.substr(0, url.indexOf(')') + 1);
                parameters[param.substr(2, param.length - 3)] = param;
                url = url.substr(param.length);
            }
            return parameters;
        }

        return {};
    }
}
